<script setup lang="ts">
import type { CorporateButtonApiModel } from '@gro-up/api/corporate'
import { Typography, RichText, Button, Link } from '@gro-up/ui'
import { useResponsive } from '@gro-up/ui/composables'
import { isUmbracoRichText } from '@gro-up/ui/utils'

export type ContactAddressProps = {
  type: 'contact'
  title: string
  content: unknown
  buttons?: CorporateButtonApiModel[]
  hideTitle?: boolean
  mapView: {
    initialCenter: [number, number]
    initialZoom?: number
  }
}

const { title, content, buttons, hideTitle } = defineProps<ContactAddressProps>()

const { isMaxLg } = useResponsive()
</script>

<template>
  <div class="flex flex-col gap-4">
    <Typography
      v-if="!hideTitle"
      as="header"
      variant="header-5">{{ title }}</Typography>
    <div v-if="isMaxLg && $slots.map" class="h-48 rounded-lg overflow-hidden">
      <slot name="map" />
    </div>
    <RichText v-if="isUmbracoRichText(content)" :text="content" body-variant="caption" />
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div v-else-if="typeof content === 'string'" class="text-[14px] leading-[20px]" v-html="content"/>
    <div v-if="buttons && buttons.length > 0" class="flex gap-2.5">
      <template v-for="(button, index) in buttons" :key="`contact-content-button-${index}`">
        <Link
          v-if="button.variant === 'textlink'"
          as="NuxtLink"
          variant="caption"
          underline
          :to="button.url"
          :target="button.openInNewTab ? '_blank' : undefined">
          {{ button.title }}
        </Link>
        <Button
          v-else
          :variant="button.variant === 'primary' ? 'primary' : 'secondary'"
          size="xs"
          as="NuxtLink"
          :to="button.url"
          :external="button.openInNewTab">
          {{ button.title }}
        </Button>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  // Only apply underlines to links within content, not to buttons
  .text-\[14px\] :deep(a),
  :deep(.rich-text a) {
    text-decoration: underline;
    color: inherit;
  }
  .text-\[14px\] :deep(a:hover),
  :deep(.rich-text a:hover) {
    text-decoration: underline;
  }
</style>
